import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Loader2, RotateCcw, Sparkles, Move, X, ZoomIn, ZoomOut, Maximize2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchBoardLayoutPhotoBlobUrl,
  fetchTrainingCardSourcePhotoBlobUrl,
  getBoardLayout,
  adjustTrainingCardFrameAndRerun,
} from '../services/api';

/**
 * Manually nudge a training card's frame on the original layout
 * photo, then re-run the AI extraction on the new crop.
 *
 * Coord space: x / y / w / h are PERCENTAGES of the same 4:3
 * `object-contain` canvas the BoardLayoutEditor uses, so the bytes
 * the backend cuts here are pixel-identical to what the layout
 * editor saw the engineer place.
 *
 * Interaction:
 *   - One finger / mouse drag inside the box body → pan the bbox.
 *   - One finger / mouse drag on a corner handle → resize.
 *   - Two-finger pinch / mouse wheel → zoom the canvas in/out.
 *   - Two-finger drag → pan the zoomed canvas.
 *   - Toolbar +/-/Reset zoom buttons mirror the gesture for desktop.
 */
const MIN_ZOOM = 1;
const MAX_ZOOM = 6;

const CertTrainFrameAdjuster = ({ open, onOpenChange, card, onApplied, prefetchedPhotoUrl }) => {
  const containerRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  // Original layout-saved coords (used by the Reset button).
  const [originalCoords, setOriginalCoords] = useState(null);
  // Live coords being edited.
  const [coords, setCoords] = useState(null);
  const [busy, setBusy] = useState(false);

  // Canvas viewport — the photo + bbox layer is wrapped in a
  // transformed div with `transform: translate(panX, panY) scale(zoom)`.
  // pan is in container-pixels, zoom is unitless. (1, 0, 0) = identity.
  const [viewport, setViewport] = useState({ zoom: 1, panX: 0, panY: 0 });
  const viewportRef = useRef(viewport);
  useEffect(() => { viewportRef.current = viewport; }, [viewport]);

  // Active pointers indexed by pointerId so we can support multitouch.
  // Single-finger drag (move/resize bbox) when size===1, two-finger
  // pinch+pan when size===2.
  const pointersRef = useRef(new Map());
  // Single-pointer drag state for bbox move/resize.
  const dragRef = useRef(null);
  // Two-pointer pinch state captured at the moment the second finger
  // touches down.
  const pinchRef = useRef(null);
  // Single-tap tracker for double-tap-to-zoom. We only register a tap
  // when the pointer goes down + up quickly without moving — so the
  // engineer's deliberate single-finger drag of the bbox body never
  // gets misclassified as a tap.
  const tapStartRef = useRef(null);
  const lastTapRef = useRef(null);

  // Diagnostics — when the photo load fails or hangs, the dialog
  // shows a small expandable diagnostic block so the engineer can
  // tell us EXACTLY what the network did. Cleared on every fresh
  // attempt.
  const [photoDiag, setPhotoDiag] = useState([]);
  // Bumped by the Retry button to re-trigger the load effect.
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!open || !card?.layoutId || !card?.frameId) return;
    let cancelled = false;
    let blobToRevoke = null;
    const abortCtl = new AbortController();
    setLoadingPhoto(!prefetchedPhotoUrl);
    setPhotoError(null);
    setPhotoDiag([]);
    // Diag entries are kept in a ref so the watchdog timeout can
    // surface them even after `cancelled` flips — without the ref
    // we'd skip the diag append on the cancelled path and the user
    // would see an empty diagnostic block.
    const diagRef = { current: [] };
    const diag = (msg) => {
      // eslint-disable-next-line no-console
      console.log('[adjuster] ' + msg);
      diagRef.current.push(msg);
      if (!cancelled) setPhotoDiag([...diagRef.current]);
    };

    // Hard 15-s timeout safety net so a misbehaving backend / ingress
    // can never lock the engineer out staring at a spinner forever.
    // The timeout flips state to an error, ABORTS the in-flight fetch,
    // and surfaces whatever diag entries were collected up to that
    // point so the user can see exactly which step hung.
    const watchdog = setTimeout(() => {
      if (cancelled) return;
      diag('TIMEOUT — aborting in-flight fetch after 15 s');
      cancelled = true;
      try { abortCtl.abort(); } catch (_) { /* noop */ }
      setPhotoDiag([...diagRef.current]);
      setPhotoError(
        'Photo load timed out after 15 s. The server may be slow or unreachable — tap Retry, or hit Cancel and submit the AI guess as-is.',
      );
      setLoadingPhoto(false);
    }, 15000);

    (async () => {
      try {
        let url = prefetchedPhotoUrl || null;
        if (url) diag('using prefetched blob URL · skipping /source-photo fetch');
        let lastErr = null;
        if (!url && card.id) {
          const t0 = performance.now();
          try {
            url = await fetchTrainingCardSourcePhotoBlobUrl(card.id, { signal: abortCtl.signal });
            diag(`/source-photo OK in ${Math.round(performance.now() - t0)} ms`);
          } catch (e) {
            lastErr = e;
            diag(`/source-photo FAILED in ${Math.round(performance.now() - t0)} ms — ${e?.message || e}`);
          }
        }
        if (!url) {
          const t0 = performance.now();
          try {
            url = await fetchBoardLayoutPhotoBlobUrl(card.layoutId);
            diag(`/board-layouts/.../photo OK in ${Math.round(performance.now() - t0)} ms`);
          } catch (e) {
            lastErr = lastErr || e;
            diag(`/board-layouts/.../photo FAILED in ${Math.round(performance.now() - t0)} ms — ${e?.message || e}`);
          }
        }
        if (!url) {
          throw lastErr || new Error('No photo available on either endpoint');
        }
        const tL0 = performance.now();
        const layout = await getBoardLayout(card.layoutId);
        diag(`/board-layouts/${card.layoutId} OK in ${Math.round(performance.now() - tL0)} ms`);
        if (cancelled) {
          if (url !== prefetchedPhotoUrl) URL.revokeObjectURL(url);
          return;
        }
        if (url !== prefetchedPhotoUrl) blobToRevoke = url;
        setPhotoUrl(url);
        const frame = (layout.frames || []).find((f) => f.id === card.frameId);
        if (!frame) {
          setPhotoError('Frame not found on layout — re-create the queue to fix.');
          return;
        }
        const seed = {
          x: Number(frame.x) || 0,
          y: Number(frame.y) || 0,
          w: Number(frame.w) || 1,
          h: Number(frame.h) || 1,
        };
        setOriginalCoords(seed);
        setCoords(seed);
        setViewport({ zoom: 1, panX: 0, panY: 0 });
      } catch (e) {
        if (!cancelled) {
          setPhotoError(e?.message || 'Could not load source photo');
        }
      } finally {
        clearTimeout(watchdog);
        if (!cancelled) setLoadingPhoto(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      try { abortCtl.abort(); } catch (_) { /* noop */ }
      if (blobToRevoke) URL.revokeObjectURL(blobToRevoke);
    };
  }, [open, card?.id, card?.layoutId, card?.frameId, prefetchedPhotoUrl, retryCount]);

  // Clear local state on close so the next open re-fetches cleanly.
  // Only revoke the blob URL if we created it ourselves — the
  // pre-fetched URL is owned by the parent component and revoked
  // there.
  useEffect(() => {
    if (open) return;
    setPhotoUrl((u) => {
      if (u && u !== prefetchedPhotoUrl) URL.revokeObjectURL(u);
      return null;
    });
    setOriginalCoords(null);
    setCoords(null);
    setPhotoError(null);
    setViewport({ zoom: 1, panX: 0, panY: 0 });
    pointersRef.current.clear();
    dragRef.current = null;
    pinchRef.current = null;
    tapStartRef.current = null;
    lastTapRef.current = null;
  }, [open]);

  // ---------- viewport math ----------

  // Clamp pan so the photo always covers the canvas (no empty
  // background leaking in). At zoom z the photo's drawn size is
  // rect × z; pan range is [-(z-1)*rect, 0] on each axis.
  const clampPan = (z, panX, panY, rect) => {
    if (!rect) return { panX, panY };
    const xMin = -((z - 1) * rect.width);
    const yMin = -((z - 1) * rect.height);
    return {
      panX: Math.max(xMin, Math.min(0, panX)),
      panY: Math.max(yMin, Math.min(0, panY)),
    };
  };

  // Convert a client-coords mouse/touch event to canvas-percent
  // coordinates (0-100), accounting for the current zoom + pan.
  const clientToCanvasPct = (clientX, clientY) => {
    const node = containerRef.current;
    if (!node) return null;
    const rect = node.getBoundingClientRect();
    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    const { zoom, panX, panY } = viewportRef.current;
    const cx = (mx - panX) / zoom;
    const cy = (my - panY) / zoom;
    return { x: (cx / rect.width) * 100, y: (cy / rect.height) * 100 };
  };

  // Apply a zoom delta around an anchor point (in container-pixel
  // coords). Used by both pinch and mouse-wheel.
  const applyZoomAt = (newZoom, anchorPx) => {
    const node = containerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
    setViewport((v) => {
      // World point under the anchor BEFORE the zoom change:
      //   world = (anchor - pan) / zoom
      // After the zoom change we want the same world point under the
      // same anchor, so:
      //   newPan = anchor - world * newZoom
      const worldX = (anchorPx.x - v.panX) / v.zoom;
      const worldY = (anchorPx.y - v.panY) / v.zoom;
      let panX = anchorPx.x - worldX * z;
      let panY = anchorPx.y - worldY * z;
      ({ panX, panY } = clampPan(z, panX, panY, rect));
      return { zoom: z, panX, panY };
    });
  };

  // ---------- pointer event routing ----------

  const onCanvasPointerDown = (e) => {
    const node = containerRef.current;
    if (!node) return;
    e.target.setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 1) {
      // First finger down — start a single-pointer drag if the target
      // carries a drag-kind data attribute, otherwise no-op (any
      // single-finger non-bbox drag is ignored — engineers pan the
      // canvas with two fingers, not one).
      const dragKind = e.target.closest('[data-drag-kind]')?.dataset.dragKind;
      const start = clientToCanvasPct(e.clientX, e.clientY);
      if (dragKind && start && coords) {
        dragRef.current = {
          kind: dragKind,
          startCanvasPct: start,
          startCoords: { ...coords },
        };
      }
      // Tap tracker for double-tap-to-zoom. We mark this as a tap
      // candidate; a real drag will set `moved` true on pointermove
      // and disqualify it.
      tapStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        time: performance.now(),
        pointerId: e.pointerId,
        moved: false,
      };
    } else if (pointersRef.current.size === 2) {
      // Second finger down — flip into pinch mode and cancel any
      // in-progress single-finger drag so the bbox doesn't snap as
      // the engineer goes from one finger to two.
      dragRef.current = null;
      tapStartRef.current = null;  // two fingers is never a tap
      const pts = Array.from(pointersRef.current.values());
      const rect = node.getBoundingClientRect();
      const cx = (pts[0].x + pts[1].x) / 2 - rect.left;
      const cy = (pts[0].y + pts[1].y) / 2 - rect.top;
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinchRef.current = {
        startDist: Math.max(1, dist),
        startCenter: { x: cx, y: cy },
        startViewport: { ...viewportRef.current },
      };
    }
  };

  const onCanvasPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Disqualify the tap as soon as the pointer drifts more than a
    // few pixels — that's a deliberate drag, not a tap.
    const tap = tapStartRef.current;
    if (tap && tap.pointerId === e.pointerId && !tap.moved) {
      const dx = e.clientX - tap.x;
      const dy = e.clientY - tap.y;
      if ((dx * dx + dy * dy) > 64) tap.moved = true;  // > 8px²
    }

    if (pointersRef.current.size === 2 && pinchRef.current) {
      const node = containerRef.current;
      const rect = node.getBoundingClientRect();
      const pts = Array.from(pointersRef.current.values());
      const cx = (pts[0].x + pts[1].x) / 2 - rect.left;
      const cy = (pts[0].y + pts[1].y) / 2 - rect.top;
      const dist = Math.max(1, Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y));
      const ratio = dist / pinchRef.current.startDist;
      const sv = pinchRef.current.startViewport;
      let z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, sv.zoom * ratio));
      // World point at the start centroid (under sv):
      const worldX = (pinchRef.current.startCenter.x - sv.panX) / sv.zoom;
      const worldY = (pinchRef.current.startCenter.y - sv.panY) / sv.zoom;
      // Want that world point under the CURRENT centroid at the new zoom:
      let panX = cx - worldX * z;
      let panY = cy - worldY * z;
      ({ panX, panY } = clampPan(z, panX, panY, rect));
      setViewport({ zoom: z, panX, panY });
      return;
    }

    if (pointersRef.current.size === 1 && dragRef.current && coords) {
      const cur = clientToCanvasPct(e.clientX, e.clientY);
      if (!cur) return;
      const drag = dragRef.current;
      const dx = cur.x - drag.startCanvasPct.x;
      const dy = cur.y - drag.startCanvasPct.y;
      const s = drag.startCoords;
      let nx = s.x;
      let ny = s.y;
      let nw = s.w;
      let nh = s.h;
      const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
      if (drag.kind === 'move') {
        nx = clamp(s.x + dx, 0, 100 - s.w);
        ny = clamp(s.y + dy, 0, 100 - s.h);
      } else {
        const minSize = 1;
        if (drag.kind === 'nw') {
          nx = clamp(s.x + dx, 0, s.x + s.w - minSize);
          ny = clamp(s.y + dy, 0, s.y + s.h - minSize);
          nw = s.x + s.w - nx;
          nh = s.y + s.h - ny;
        } else if (drag.kind === 'ne') {
          nw = clamp(s.w + dx, minSize, 100 - s.x);
          ny = clamp(s.y + dy, 0, s.y + s.h - minSize);
          nh = s.y + s.h - ny;
        } else if (drag.kind === 'sw') {
          nx = clamp(s.x + dx, 0, s.x + s.w - minSize);
          nw = s.x + s.w - nx;
          nh = clamp(s.h + dy, minSize, 100 - s.y);
        } else if (drag.kind === 'se') {
          nw = clamp(s.w + dx, minSize, 100 - s.x);
          nh = clamp(s.h + dy, minSize, 100 - s.y);
        }
      }
      setCoords({ x: nx, y: ny, w: nw, h: nh });
    }
  };

  const onCanvasPointerEnd = (e) => {
    // Tap detection — only the originating pointer can complete a tap,
    // and only if it hasn't moved (drag would have flipped `moved`).
    const tap = tapStartRef.current;
    let isTap = false;
    let tapAt = null;
    if (tap && tap.pointerId === e.pointerId && !tap.moved) {
      const dt = performance.now() - tap.time;
      if (dt < 250) {
        isTap = true;
        tapAt = { x: e.clientX, y: e.clientY, time: performance.now() };
      }
    }
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) {
      pinchRef.current = null;
    }
    if (pointersRef.current.size === 0) {
      dragRef.current = null;
      tapStartRef.current = null;
    }

    if (!isTap || !tapAt) return;

    // Double-tap detection: a second tap within 320ms and within
    // 35px of the first triggers the zoom toggle. Otherwise this
    // becomes the new "first tap" we're waiting on.
    const last = lastTapRef.current;
    if (last && (tapAt.time - last.time) < 320) {
      const dx = tapAt.x - last.x;
      const dy = tapAt.y - last.y;
      if ((dx * dx + dy * dy) < 1225) {  // < 35px²
        // Double-tap fired — toggle zoom anchored at the tap point.
        const node = containerRef.current;
        if (node) {
          const rect = node.getBoundingClientRect();
          const anchorPx = { x: tapAt.x - rect.left, y: tapAt.y - rect.top };
          const cur = viewportRef.current.zoom;
          if (cur > 1.5) {
            // Already zoomed → fit-to-canvas.
            setViewport({ zoom: 1, panX: 0, panY: 0 });
          } else {
            // Zoom in to a comfortable 3× anchored at the tap point.
            applyZoomAt(3, anchorPx);
          }
        }
        lastTapRef.current = null;
        return;
      }
    }
    lastTapRef.current = tapAt;
  };

  // Mouse wheel zoom — Ctrl-wheel and plain wheel both zoom (matches
  // common image-editor convention). Pinch trackpad gestures arrive
  // as ctrlKey-wheel events so this also covers MacBook trackpad.
  const onCanvasWheel = (e) => {
    e.preventDefault();
    const node = containerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const anchorPx = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const factor = Math.exp(-e.deltaY * 0.0015);
    const newZoom = viewportRef.current.zoom * factor;
    applyZoomAt(newZoom, anchorPx);
  };

  // The browser's default `passive: true` listener means
  // `e.preventDefault()` inside `onWheel` is silently ignored. Attach
  // a non-passive native listener so wheel zoom doesn't also scroll
  // the dialog page.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const handler = (e) => onCanvasWheel(e);
    node.addEventListener('wheel', handler, { passive: false });
    return () => node.removeEventListener('wheel', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoUrl, coords]);

  // Toolbar zoom buttons — same math as wheel but anchored at the
  // canvas centre.
  const stepZoom = (mult) => {
    const node = containerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    applyZoomAt(viewportRef.current.zoom * mult, { x: rect.width / 2, y: rect.height / 2 });
  };
  const resetZoom = () => setViewport({ zoom: 1, panX: 0, panY: 0 });

  // ---------- save flow ----------

  const reset = () => {
    if (originalCoords) setCoords(originalCoords);
  };

  const apply = async () => {
    if (!coords || !card?.id || busy) return;
    setBusy(true);
    try {
      const res = await adjustTrainingCardFrameAndRerun(card.id, {
        x: Number(coords.x.toFixed(3)),
        y: Number(coords.y.toFixed(3)),
        w: Number(coords.w.toFixed(3)),
        h: Number(coords.h.toFixed(3)),
      });
      toast.success(
        res.zoomApplied
          ? 'Crop adjusted · AI re-extracted on the new region'
          : 'Crop adjusted · AI returned no values for the new region',
      );
      onApplied?.(res.card);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Re-extract failed');
    } finally {
      setBusy(false);
    }
  };

  // Keyboard: R re-runs the AI from inside the dialog so the engineer
  // can iterate "drag → R → drag → R" without round-tripping to the
  // mouse for the Re-run button. Only fires when the dialog is open
  // and the user isn't typing in an input.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.key === 'r' || e.key === 'R') && !busy && coords && !loadingPhoto && !photoError) {
        e.preventDefault();
        apply();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy, coords, loadingPhoto, photoError]);

  // ---------- render ----------

  const boxStyle = useMemo(() => {
    if (!coords) return null;
    return {
      left: coords.x + '%',
      top: coords.y + '%',
      width: coords.w + '%',
      height: coords.h + '%',
    };
  }, [coords]);

  // Counter-scale border + handle visuals against the canvas zoom so
  // they stay crisp at every zoom level (a 2 px border becomes a 12 px
  // border at zoom=6 otherwise).
  const counterScale = 1 / viewport.zoom;
  const borderWidth = `${2 * counterScale}px`;

  const cornerStyle = (corner) => {
    const size = 14 * counterScale;
    const offset = -size / 2;
    const base = {
      position: 'absolute',
      width: size,
      height: size,
      background: 'white',
      border: `${2 * counterScale}px solid #2563eb`,
      borderRadius: 3 * counterScale,
      touchAction: 'none',
    };
    if (corner === 'nw') return { ...base, left: offset, top: offset, cursor: 'nwse-resize' };
    if (corner === 'ne') return { ...base, right: offset, top: offset, cursor: 'nesw-resize' };
    if (corner === 'sw') return { ...base, left: offset, bottom: offset, cursor: 'nesw-resize' };
    return { ...base, right: offset, bottom: offset, cursor: 'nwse-resize' };
  };

  const transformStyle = {
    transform: `translate(${viewport.panX}px, ${viewport.panY}px) scale(${viewport.zoom})`,
    transformOrigin: '0 0',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="cert-train-adjuster-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Move className="h-4 w-4 text-blue-700" />
            Adjust crop &amp; re-run AI
            {card?.frameLabel && (
              <span className="text-xs font-mono uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                {card.frameLabel}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-slate-600 -mt-2 mb-2 leading-relaxed">
          Drag the box to move, drag corners to resize. <span className="font-medium">Pinch with two fingers</span> on
          touch devices, <span className="font-medium">double-tap</span> to zoom to 3× at that point (double-tap again to fit), or use the mouse wheel / zoom buttons.
          The new coords are saved onto the layout and the AI re-runs only on this card.
        </div>

        {loadingPhoto && (
          <div className="aspect-[4/3] w-full flex items-center justify-center bg-slate-900 text-white rounded">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading photo…
          </div>
        )}

        {photoError && !loadingPhoto && (
          <div
            className="aspect-[4/3] w-full flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded text-sm p-4 text-center gap-3"
            data-testid="cert-train-adjuster-error"
          >
            <div className="text-red-700 font-medium">{photoError}</div>
            <Button
              type="button"
              size="sm"
              onClick={() => setRetryCount((n) => n + 1)}
              data-testid="cert-train-adjuster-retry-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> Retry
            </Button>
            {photoDiag.length > 0 && (
              <details open className="text-left text-[11px] text-slate-600 font-mono w-full max-w-md">
                <summary className="cursor-pointer text-slate-500">Diagnostics ({photoDiag.length} step{photoDiag.length === 1 ? '' : 's'})</summary>
                <ul className="mt-2 space-y-1 break-all">
                  {photoDiag.map((m, i) => <li key={i}>· {m}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        {!loadingPhoto && !photoError && photoUrl && coords && (
          <div className="relative">
            <div
              ref={containerRef}
              className="relative w-full bg-slate-900 rounded overflow-hidden select-none"
              style={{ aspectRatio: '4 / 3', touchAction: 'none' }}
              onPointerDown={onCanvasPointerDown}
              onPointerMove={onCanvasPointerMove}
              onPointerUp={onCanvasPointerEnd}
              onPointerCancel={onCanvasPointerEnd}
              onPointerLeave={onCanvasPointerEnd}
              data-testid="cert-train-adjuster-canvas"
            >
              {/* Zoomed/panned layer — photo + bbox share this transform
                  so the bbox stays anchored to the photo at all zooms. */}
              <div
                className="absolute inset-0"
                style={transformStyle}
                data-testid="cert-train-adjuster-zoom-layer"
              >
                <img
                  src={photoUrl}
                  alt="Layout reference"
                  className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                  draggable={false}
                />
                {/* Bounding box. Body moves on drag, corners resize. */}
                <div
                  className="absolute bg-blue-500/15"
                  style={{
                    ...boxStyle,
                    border: `${borderWidth} solid #3b82f6`,
                    cursor: 'move',
                    touchAction: 'none',
                  }}
                  data-drag-kind="move"
                  data-testid="cert-train-adjuster-box"
                >
                  <div data-drag-kind="nw" style={cornerStyle('nw')} />
                  <div data-drag-kind="ne" style={cornerStyle('ne')} />
                  <div data-drag-kind="sw" style={cornerStyle('sw')} />
                  <div data-drag-kind="se" style={cornerStyle('se')} />
                </div>
              </div>

              {/* Live coords readout (top-right, faint, above transform). */}
              <div className="absolute top-2 right-2 px-2 py-1 rounded bg-black/60 text-white text-[10px] font-mono pointer-events-none">
                x {coords.x.toFixed(1)}% · y {coords.y.toFixed(1)}% · w {coords.w.toFixed(1)}% · h {coords.h.toFixed(1)}%
              </div>

              {/* Zoom toolbar (bottom-right, above transform). */}
              <div
                className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/60 rounded p-1"
                data-testid="cert-train-adjuster-zoom-toolbar"
              >
                <button
                  type="button"
                  onClick={() => stepZoom(1 / 1.4)}
                  disabled={viewport.zoom <= MIN_ZOOM + 0.001}
                  className="h-7 w-7 rounded text-white hover:bg-white/15 disabled:opacity-30 flex items-center justify-center"
                  title="Zoom out"
                  data-testid="cert-train-adjuster-zoom-out-btn"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <span className="text-white text-[11px] font-mono w-10 text-center tabular-nums" data-testid="cert-train-adjuster-zoom-level">
                  {viewport.zoom.toFixed(1)}×
                </span>
                <button
                  type="button"
                  onClick={() => stepZoom(1.4)}
                  disabled={viewport.zoom >= MAX_ZOOM - 0.001}
                  className="h-7 w-7 rounded text-white hover:bg-white/15 disabled:opacity-30 flex items-center justify-center"
                  title="Zoom in"
                  data-testid="cert-train-adjuster-zoom-in-btn"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={resetZoom}
                  disabled={viewport.zoom === 1 && viewport.panX === 0 && viewport.panY === 0}
                  className="h-7 w-7 rounded text-white hover:bg-white/15 disabled:opacity-30 flex items-center justify-center"
                  title="Fit to canvas"
                  data-testid="cert-train-adjuster-zoom-reset-btn"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="cert-train-adjuster-cancel-btn"
          >
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={reset}
            disabled={busy || !originalCoords || !coords
              || (originalCoords.x === coords.x && originalCoords.y === coords.y
                && originalCoords.w === coords.w && originalCoords.h === coords.h)}
            data-testid="cert-train-adjuster-reset-btn"
          >
            <RotateCcw className="h-4 w-4 mr-1" /> Reset
          </Button>
          <Button
            type="button"
            onClick={apply}
            disabled={busy || !coords || loadingPhoto || !!photoError}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="cert-train-adjuster-apply-btn"
            title="Re-run AI on the new region — shortcut: R"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            Re-run AI
            <kbd className="ml-2 px-1 rounded bg-white/20 border border-white/30 text-[10px] font-mono">R</kbd>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CertTrainFrameAdjuster;
